// ============================================================
// Phexotial — Page Capture Service
// ============================================================
// Reusable capture pipeline that validates URLs, normalizes
// them, detects duplicates, and persists to the correct
// existing domain record (Reading Queue or Bookmarks).
// ============================================================

import { nanoid } from 'nanoid';
import type { Bookmark, ReadingQueueItem } from '@/types';
import {
  saveBookmark,
  getBookmarks,
  saveReadingQueueItem,
  getReadingQueue,
} from '@/storage/db';
import { isValidHttpUrl } from '@/config';

// --- Types ---

export type CaptureDestination = 'reading-queue' | 'bookmarks';

export interface CaptureRequest {
  url: string;
  title: string;
  faviconUrl?: string;
  source?: string;
}

export type CaptureResult =
  | { status: 'created'; destination: CaptureDestination; recordId: string }
  | { status: 'duplicate'; destination: CaptureDestination; recordId?: string }
  | { status: 'unsupported-url'; reason: string }
  | { status: 'failed'; reason: string };

// --- URL Normalization ---

export function normalizeUrl(url: string): string {
  try {
    const u = new URL(url.trim());
    u.hash = '';
    if (u.protocol === 'https:' && u.port === '443') u.port = '';
    if (u.protocol === 'http:' && u.port === '80') u.port = '';
    return u.toString().replace(/\/$/, '');
  } catch {
    return url.trim();
  }
}

// --- URL Validation ---

const BLOCKED_PROTOCOLS = [
  'edge:',
  'chrome:',
  'about:',
  'file:',
  'view-source:',
  'data:',
  'javascript:',
  'chrome-extension:',
  'edge-extension:',
  'moz-extension:',
  'blob:',
];

export function isCapturableUrl(url: string): { capturable: boolean; reason?: string } {
  if (!url || typeof url !== 'string') {
    return { capturable: false, reason: 'No URL provided' };
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return { capturable: false, reason: 'Empty URL' };
  }

  try {
    const u = new URL(trimmed);
    if (BLOCKED_PROTOCOLS.includes(u.protocol)) {
      return { capturable: false, reason: `Cannot capture ${u.protocol} pages` };
    }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return { capturable: false, reason: `Unsupported protocol: ${u.protocol}` };
    }
    return { capturable: true };
  } catch {
    return { capturable: false, reason: 'Invalid URL' };
  }
}

export function isExtensionPageUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol.startsWith('chrome-extension:') || u.protocol.startsWith('edge-extension:') || u.protocol.startsWith('moz-extension:');
  } catch {
    return false;
  }
}

// --- Duplicate Detection ---

export async function findDuplicateBookmark(normalizedUrl: string): Promise<Bookmark | undefined> {
  const bookmarks = await getBookmarks();
  return bookmarks.find((b) => normalizeUrl(b.url) === normalizedUrl);
}

export async function findDuplicateReadingQueue(url: string): Promise<ReadingQueueItem | undefined> {
  const items = await getReadingQueue();
  const normalized = normalizeUrl(url);
  return items.find((i) => i.url && normalizeUrl(i.url) === normalized);
}

// --- Capture ---

export async function capturePage(
  request: CaptureRequest,
  destination: CaptureDestination,
): Promise<CaptureResult> {
  const { url, title } = request;

  const validation = isCapturableUrl(url);
  if (!validation.capturable) {
    return { status: 'unsupported-url', reason: validation.reason ?? 'Unsupported URL' };
  }

  if (!isValidHttpUrl(url.trim())) {
    return { status: 'unsupported-url', reason: 'URL must be http: or https:' };
  }

  const normalized = normalizeUrl(url);
  const now = new Date().toISOString();
  const cleanTitle = title.trim() || url.trim();

  try {
    if (destination === 'bookmarks') {
      const existing = await findDuplicateBookmark(normalized);
      if (existing) {
        return { status: 'duplicate', destination: 'bookmarks', recordId: existing.id };
      }

      const bookmark: Bookmark = {
        id: nanoid(12),
        url: normalized,
        title: cleanTitle,
        faviconUrl: request.faviconUrl,
        folders: [],
        tags: request.source ? [request.source] : [],
        pinned: false,
        createdAt: now,
        updatedAt: now,
      };

      await saveBookmark(bookmark);
      return { status: 'created', destination: 'bookmarks', recordId: bookmark.id };
    }

    if (destination === 'reading-queue') {
      const existing = await findDuplicateReadingQueue(url);
      if (existing) {
        return { status: 'duplicate', destination: 'reading-queue', recordId: existing.id };
      }

      const queue = await getReadingQueue();
      const maxPosition = queue.reduce((max, q) => Math.max(max, q.position), 0);

      const queueItem: ReadingQueueItem = {
        id: nanoid(12),
        itemId: `ext-${nanoid(12)}`,
        providerId: 'extension-capture',
        title: cleanTitle,
        url: normalized,
        sourceName: request.source ?? 'extension-capture',
        thumbnailUrl: undefined,
        summary: undefined,
        tags: request.source ? [request.source] : [],
        addedAt: now,
        status: 'queued',
        position: maxPosition + 1,
      };

      await saveReadingQueueItem(queueItem);
      return { status: 'created', destination: 'reading-queue', recordId: queueItem.id };
    }

    return { status: 'failed', reason: `Unknown destination: ${destination}` };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown database error';
    return { status: 'failed', reason: message };
  }
}

// --- Preferences ---

const PREF_KEY = 'phexotial-capture-prefs';

export interface CapturePrefs {
  defaultDestination: CaptureDestination;
  closePopupOnCapture: boolean;
  showCaptureConfirmation: boolean;
}

export const DEFAULT_CAPTURE_PREFS: CapturePrefs = {
  defaultDestination: 'reading-queue',
  closePopupOnCapture: false,
  showCaptureConfirmation: true,
};

export async function getCapturePrefs(): Promise<CapturePrefs> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.sync?.get) {
      const result = await chrome.storage.sync.get(PREF_KEY);
      const stored = result[PREF_KEY] as Partial<CapturePrefs> | undefined;
      if (stored) {
        return { ...DEFAULT_CAPTURE_PREFS, ...stored };
      }
    }
  } catch {
    // storage API unavailable (test env, web context)
  }
  return { ...DEFAULT_CAPTURE_PREFS };
}

export async function setCapturePrefs(prefs: Partial<CapturePrefs>): Promise<void> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.sync?.set) {
      const current = await getCapturePrefs();
      const merged = { ...current, ...prefs };
      await chrome.storage.sync.set({ [PREF_KEY]: merged });
    }
  } catch {
    // storage API unavailable
  }
}

